import { WEEKDAYS_LONG, WEEKDAYS_SHORT, formatTime } from '@/lib/utils';

export const VIEW_TYPES = [
  { id: 'resources-vertical', label: 'Ressourcen - vertikal', axis: 'rooms', span: 'day' },
  { id: 'rooms-day', label: 'Räume - Tag', axis: 'rooms', span: 'day' },
  { id: 'rooms-week', label: 'Räume - Woche', axis: 'rooms', span: 'week' },
  { id: 'rooms-month', label: 'Räume - Monat', axis: 'rooms', span: 'month' },
  { id: 'teachers-day', label: 'Lehrkräfte - Tag', axis: 'teachers', span: 'day' },
  { id: 'teachers-week', label: 'Lehrkräfte - Woche', axis: 'teachers', span: 'week' },
  { id: 'teachers-month', label: 'Lehrkräfte - Monat', axis: 'teachers', span: 'month' },
];

export const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const parseISO = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const minutes = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = String(hhmm).split(/[:.]/).map(Number);
  return (h || 0) * 60 + (m || 0);
};

export const getKW = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
};

export const mondayOf = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};

export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export const monthGrid = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const start = mondayOf(first);
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const days = [];
    for (let i = 0; i < 7; i++) days.push(addDays(start, w * 7 + i));
    weeks.push(days);
  }
  return weeks;
};

export const headlineDate = (d) => {
  const wd = WEEKDAYS_LONG[(d.getDay() + 6) % 7].toUpperCase();
  const months = ['JANUAR', 'FEBRUAR', 'MÄRZ', 'APRIL', 'MAI', 'JUNI', 'JULI', 'AUGUST', 'SEPTEMBER', 'OKTOBER', 'NOVEMBER', 'DEZEMBER'];
  return `${wd}, ${String(d.getDate()).padStart(2, '0')}. ${months[d.getMonth()]} ${d.getFullYear()}`;
};

/** Räume-Woche: die Spalten sind die Wochentage des einen gewählten Raums. */
export function roomsWeekColumns(currentDate, room, entries) {
  const monday = mondayOf(currentDate);
  return Array.from({ length: 7 }, (_, index) => {
    const day = addDays(monday, index);
    const iso = toISO(day);
    return {
      id: iso,
      label: weekHeader(day),
      date: iso,
      location_id: room?.location_id,
      location_name: room?.location_name,
      room_name: room?.name,
      room_id: room?.id,
      entries: (entries || []).filter((entry) => (
        entry.date === iso && (!room?.id || entry.room_id === room.id)
      )),
    };
  });
}

export const weekHeader = (d) => {
  const idx = (d.getDay() + 6) % 7;
  return `${WEEKDAYS_SHORT[idx]}. ${d.getDate()}.${d.getMonth() + 1}.`;
};

export const clockLabel = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h} Uhr`;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const fmtRange = (start, end) => {
  const a = formatTime(start);
  const b = formatTime(end);
  return a && b ? `${a} - ${b}` : a || b || '';
};

export function weekdayAbbr(iso) {
  if (!iso) return '';
  const d = parseISO(String(iso).slice(0, 10));
  if (Number.isNaN(d.getTime())) return '';
  return WEEKDAYS_SHORT[(d.getDay() + 6) % 7] || '';
}

/** „Hüneburg, Mats“ → „Mats Hüneburg“. Namen ohne Komma bleiben. */
export function studentDisplayName(name) {
  const raw = String(name || '').trim();
  const comma = raw.indexOf(',');
  if (comma < 0) return raw;
  const last = raw.slice(0, comma).trim();
  const first = raw.slice(comma + 1).trim();
  return [first, last].filter(Boolean).join(' ');
}

/** „14-tägig“ bleibt in einer Zeile „14tg“. */
export function compactLessonLabel(label) {
  return String(label || '').replace(/14\s*-?\s*t[äa]gig/gi, '14tg');
}

/** Wochentag und Uhrzeit stehen in der Kopfzeile, nicht noch einmal im Fach. */
export function subjectWithoutWhen(label) {
  return String(label || '')
    .replace(/\s+(?:Mo|Di|Mi|Do|Fr|Sa|So)\.?\s+\d{1,2}:\d{2}(?:\s*[-–]\s*\d{1,2}:\d{2})?(?:\s*Uhr)?\s*$/i, '')
    .replace(/\s+\d{1,2}:\d{2}(?:\s*[-–]\s*\d{1,2}:\d{2})?(?:\s*Uhr)?\s*$/, '')
    .trim();
}

/** Überlappende Termine nebeneinander, anstoßende hintereinander. */
export function layoutEventLanes(entries) {
  const items = (entries || []).map((e, index) => ({
    index,
    start: minutes(e.start),
    end: minutes(e.end),
  }));
  const order = [...items].sort((a, b) => a.start - b.start || a.end - b.end || a.index - b.index);
  const laneEnds = [];
  const laneOf = new Map();
  for (const item of order) {
    let lane = laneEnds.findIndex((end) => end <= item.start);
    if (lane < 0) {
      lane = laneEnds.length;
      laneEnds.push(item.end);
    } else {
      laneEnds[lane] = item.end;
    }
    laneOf.set(item.index, lane);
  }
  return { laneOf, count: Math.max(1, laneEnds.length) };
}

export const isGroupish = (e) =>
  e?.course_type === 'group' ||
  (Array.isArray(e?.students) && e.students.length > 1) ||
  /früh|rhythmus|gruppe|10er|klasse|ballett|chor|orchester/i.test(e?.name || '');

/**
 * Wählt die Hauptniederlassung eines Mandanten aus Plan-Räumen.
 * Priorität: expliziter Haupt-Name → short_code=Slug → Name≈Mandant → meiste Räume.
 */
export function pickPrimaryLocationId(rooms, tenant) {
  const byLoc = new Map();
  for (const r of rooms || []) {
    if (!r.location_id) continue;
    const cur = byLoc.get(r.location_id) || {
      id: r.location_id,
      name: r.location_name || '',
      short: r.location_short_code || '',
      count: 0,
    };
    cur.count += 1;
    if (!cur.name && r.location_name) cur.name = r.location_name;
    if (!cur.short && r.location_short_code) cur.short = r.location_short_code;
    byLoc.set(r.location_id, cur);
  }
  const locs = [...byLoc.values()];
  if (!locs.length) return null;

  const explicitHaupt = locs.find((l) => /haupt(standort|niederlassung)/i.test(l.name));
  if (explicitHaupt) return explicitHaupt.id;

  const tenantName = String(tenant?.name || '').trim().toLowerCase();
  const tenantSlug = String(tenant?.slug || '').trim().toLowerCase();
  const nameToken = tenantName.split(/\s+/).filter((t) => t.length >= 4).pop() || '';

  const score = (loc) => {
    const n = loc.name.toLowerCase();
    const short = String(loc.short || '').toLowerCase();
    let s = 0;
    if (tenantSlug && short && short === tenantSlug) s += 800;
    if (tenantName && n === tenantName) s += 700;
    if (tenantName && n && (n.includes(tenantName) || tenantName.includes(n))) s += 400;
    if (nameToken && n.includes(nameToken)) s += 300;
    s += Math.min(loc.count, 50);
    return s;
  };

  return [...locs].sort((a, b) => {
    const d = score(b) - score(a);
    if (d) return d;
    return a.name.localeCompare(b.name, 'de');
  })[0].id;
}

/** Standard: Hauptniederlassung + alle ihre Räume (erster Aufruf / ungültige Prefs). */
export function resolveCalendarLocationDefaults({
  rooms,
  locationId,
  selectedRoomIds,
  tenant,
  showAllLocations = false,
}) {
  if (showAllLocations) {
    return { locationId: null, selectedRoomIds: [] };
  }
  const list = rooms || [];
  const validIds = new Set(list.map((r) => r.id));
  const kept = (selectedRoomIds || []).filter((id) => validIds.has(id));

  let nextLoc = locationId;
  const locValid = nextLoc && list.some((r) => r.location_id === nextLoc);

  if (!locValid) {
    if (kept.length) {
      nextLoc = list.find((r) => r.id === kept[0])?.location_id
        || pickPrimaryLocationId(list, tenant)
        || null;
      return { locationId: nextLoc, selectedRoomIds: kept };
    }
    nextLoc = pickPrimaryLocationId(list, tenant) || list[0]?.location_id || null;
  }

  if (kept.length) {
    return { locationId: nextLoc, selectedRoomIds: kept };
  }

  const atLoc = list.filter((r) => r.location_id === nextLoc);
  return { locationId: nextLoc, selectedRoomIds: atLoc.map((r) => r.id) };
}

export function roomsAtLocation(rooms, locationId) {
  return (rooms || []).filter((r) => r.location_id === locationId);
}

/** all | some | none — Auswahlzustand eines Standorts. */
export function locationSelectionState(locationId, rooms, selectedRoomIds) {
  const locRoomIds = roomsAtLocation(rooms, locationId).map((r) => r.id);
  if (!locRoomIds.length) return 'none';
  const picked = new Set(selectedRoomIds || []);
  const count = locRoomIds.filter((id) => picked.has(id)).length;
  if (count === 0) return 'none';
  if (count === locRoomIds.length) return 'all';
  return 'some';
}

export function toggleLocationRooms(locationId, rooms, selectedRoomIds) {
  const locRoomIds = roomsAtLocation(rooms, locationId).map((r) => r.id);
  if (!locRoomIds.length) return selectedRoomIds || [];
  const prev = new Set(selectedRoomIds || []);
  const allOn = locRoomIds.every((id) => prev.has(id));
  if (allOn) {
    locRoomIds.forEach((id) => prev.delete(id));
  } else {
    locRoomIds.forEach((id) => prev.add(id));
  }
  return [...prev];
}

export function toggleRoomInSelection(roomId, selectedRoomIds) {
  const prev = selectedRoomIds || [];
  if (prev.includes(roomId)) return prev.filter((id) => id !== roomId);
  return [...prev, roomId];
}

/** Dieselbe Kalenderansicht, zusätzlich ohne die Verwaltungsschale. */
export function calendarWindowHref(search) {
  const params = new URLSearchParams(typeof search === 'string' ? search : (search?.toString?.() || ''));
  params.set('fenster', '1');
  return `/calendar?${params.toString()}`;
}
